import { useEffect, useRef } from 'react';
import {
  TextField,
  ToggleButton,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  FlexColumn,
} from '@elementary/common-ui/components';
import { ToggleButtonGroup } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { AthenaAuthMethod } from 'shared/types';
import { RoleArnAuth, SecretAccessKeyAuth } from '../AuthTypeFormFields';
import { getAthenaAuthMethod } from './utils';

const AthenaFormContent = ({ disabled }: { disabled?: boolean }) => {
  const selectedAuthMethodRef = useRef<any>();
  const methods = useFormContext();
  const {
    register,
    setValue,
    formState: { errors },
  } = methods;
  const [
    explicitAuthMethod,
    athenaAccessKeyId,
    athenaSecretAccessKey,
    athenaRoleArn,
    athenaExternalId,
  ] = useWatch({
    name: [
      'athenaAuthMethod',
      'athenaAccessKeyId',
      'athenaSecretAccessKey',
      'athenaRoleArn',
      'athenaExternalId',
    ],
  });
  const athenaAuthMethod = getAthenaAuthMethod({
    athenaAuthMethod: explicitAuthMethod,
    athenaAccessKeyId,
    athenaSecretAccessKey,
    athenaRoleArn,
    athenaExternalId,
  });

  useEffect(() => {
    if (athenaAuthMethod != null) {
      selectedAuthMethodRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [athenaAuthMethod, selectedAuthMethodRef]);

  return (
    <>
      <FormField
        title="Region"
        errorMessage={errors.athenaRegion?.message as string}
      >
        <TextField
          {...register('athenaRegion', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. us-east-1"
          disabled={disabled}
          size="large"
        />
      </FormField>
      <FormField
        title="S3 staging directory"
        errorMessage={errors.athenaS3StagingDir?.message as string}
      >
        <TextField
          {...register('athenaS3StagingDir', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. s3://my-bucket/path/to/elementary-queries-output/"
          disabled={disabled}
          size="large"
        />
      </FormField>
      <FormField
        title="Workgroup"
        optional
        errorMessage={errors.athenaWorkgroup?.message as string}
      >
        <TextField
          {...register('athenaWorkgroup')}
          variant="outlined"
          placeholder="e.g. analytics-workgroup"
          disabled={disabled}
          size="large"
        />
      </FormField>
      <FormField title="Select authentication method">
        <ToggleButtonGroup
          fullWidth
          value={athenaAuthMethod}
          onChange={(_, val: AthenaAuthMethod | null) => {
            if (val) {
              setValue('athenaAuthMethod', val);
            }
          }}
          exclusive
          disabled={disabled}
        >
          <ToggleButton value={AthenaAuthMethod.SECRET_ACCESS_KEY} size="large">
            Secret access key
          </ToggleButton>
          <ToggleButton value={AthenaAuthMethod.ROLE_ARN} size="large">
            AWS role ARN
          </ToggleButton>
        </ToggleButtonGroup>
      </FormField>

      <FlexColumn
        gap="inherit"
        ref={athenaAuthMethod != null ? selectedAuthMethodRef : undefined}
      >
        {athenaAuthMethod === AthenaAuthMethod.SECRET_ACCESS_KEY && (
          <SecretAccessKeyAuth
            secretKeyFormKey="athenaSecretAccessKey"
            keyIdFormKey="athenaAccessKeyId"
            sessionTokenFormKey="athenaSessionToken"
            disabled={disabled}
          />
        )}

        {athenaAuthMethod === AthenaAuthMethod.ROLE_ARN && (
          <RoleArnAuth
            roleArnFormKey="athenaRoleArn"
            externalIdFormKey="athenaExternalId"
            disabled={disabled}
          />
        )}
      </FlexColumn>
    </>
  );
};

export default AthenaFormContent;
